import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

interface DeliveryStop {
  id: string;
  address: string;
  coordinates: [number, number] | null;
  label: string;
}

interface Route {
  id: string;
  user_id: string;
  status: 'active' | 'completed';
  stops: DeliveryStop[];
  optimized?: boolean;
  original_distance?: number;
  original_duration?: number;
  optimized_distance?: number;
  optimized_duration?: number;
  started_at?: string;
  finished_at?: string;
  created_at: string;
}

interface Delivery {
  id: string;
  user_id: string;
  route_id?: string;
  amount: number;
  status: 'completed' | 'pending';
  origin?: string;
  stops: DeliveryStop[];
  total_distance?: number;
  total_duration?: number;
  optimized?: boolean;
  created_at: string;
}

interface Stats {
  todayEarnings: number;
  todayDeliveries: number;
  todayDistance: number;
  weekEarnings: number;
  monthEarnings: number;
  totalDeliveries: number;
  totalDistance: number;
}

interface DeliveryDataContextType {
  routes: Route[];
  deliveries: Delivery[];
  loading: boolean;
  stats: Stats;
  saveRoute: (routeData: Omit<Route, 'id' | 'user_id' | 'created_at'>) => Promise<Route | null>;
  updateRoute: (routeId: string, updates: Partial<Route>) => Promise<Route | null>;
  finishRoute: (routeId: string) => Promise<Delivery | null>;
  createDelivery: (deliveryData: Omit<Delivery, 'id' | 'user_id' | 'created_at'>) => Promise<Delivery | null>;
  updateDeliveryAmount: (deliveryId: string, amount: number) => Promise<Delivery | null>;
  deleteDelivery: (deliveryId: string) => Promise<boolean>;
  deleteRoute: (routeId: string) => Promise<boolean>;
  fetchRoutes: () => Promise<void>;
  fetchDeliveries: () => Promise<void>;
}

const DeliveryDataContext = createContext<DeliveryDataContextType | undefined>(undefined);

export function DeliveryDataProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [routes, setRoutes] = useState<Route[]>([]);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Usar refs para evitar re-fetches desnecessários
  const isFetchingRoutes = useRef(false);
  const isFetchingDeliveries = useRef(false);

  // Calcular estatísticas com useMemo (apenas quando deliveries mudar)
  const stats = useMemo<Stats>(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    const completedDeliveries = deliveries.filter((d) => d.status === 'completed');

    const todayDeliveries = completedDeliveries.filter(
      (d) => new Date(d.created_at) >= today
    );
    const weekDeliveries = completedDeliveries.filter(
      (d) => new Date(d.created_at) >= weekAgo
    );
    const monthDeliveries = completedDeliveries.filter(
      (d) => new Date(d.created_at) >= monthStart
    );

    return {
      todayEarnings: todayDeliveries.reduce((sum, d) => sum + (d.amount || 0), 0),
      todayDeliveries: todayDeliveries.length,
      todayDistance: todayDeliveries.reduce((sum, d) => sum + (d.total_distance || 0), 0),
      weekEarnings: weekDeliveries.reduce((sum, d) => sum + (d.amount || 0), 0),
      monthEarnings: monthDeliveries.reduce((sum, d) => sum + (d.amount || 0), 0),
      totalDeliveries: completedDeliveries.length,
      totalDistance: completedDeliveries.reduce((sum, d) => sum + (d.total_distance || 0), 0),
    };
  }, [deliveries]);

  // Buscar rotas
  const fetchRoutes = useCallback(async () => {
    if (!user || isFetchingRoutes.current) return;

    isFetchingRoutes.current = true;
    try {
      const { data, error } = await supabase
        .from('routes')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setRoutes(data || []);
    } catch (error) {
      console.error('Erro ao buscar rotas:', error);
    } finally {
      isFetchingRoutes.current = false;
    }
  }, [user]);

  // Buscar entregas
  const fetchDeliveries = useCallback(async () => {
    if (!user || isFetchingDeliveries.current) return;

    isFetchingDeliveries.current = true;
    try {
      const { data, error } = await supabase
        .from('deliveries')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setDeliveries(data || []);
    } catch (error) {
      console.error('Erro ao buscar entregas:', error);
    } finally {
      isFetchingDeliveries.current = false;
    }
  }, [user]);

  // Salvar nova rota (com optimistic update)
  const saveRoute = useCallback(
    async (routeData: Omit<Route, 'id' | 'user_id' | 'created_at'>) => {
      if (!user) return null;

      try {
        const { data, error } = await supabase
          .from('routes')
          .insert({
            user_id: user.id,
            ...routeData,
          })
          .select()
          .single();

        if (error) throw error;

        // Optimistic update
        setRoutes((prev) => [data, ...prev]);
        return data;
      } catch (error) {
        console.error('Erro ao salvar rota:', error);
        // Fallback: refetch
        await fetchRoutes();
        return null;
      }
    },
    [user, fetchRoutes]
  );

  // Atualizar rota (com optimistic update)
  const updateRoute = useCallback(
    async (routeId: string, updates: Partial<Route>) => {
      if (!user) return null;

      // Optimistic update
      setRoutes((prev) =>
        prev.map((route) =>
          route.id === routeId ? { ...route, ...updates } : route
        )
      );

      try {
        const { data, error } = await supabase
          .from('routes')
          .update(updates)
          .eq('id', routeId)
          .eq('user_id', user.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } catch (error) {
        console.error('Erro ao atualizar rota:', error);
        // Fallback: refetch
        await fetchRoutes();
        return null;
      }
    },
    [user, fetchRoutes]
  );

  // Finalizar rota e criar entrega (com optimistic update)
  const finishRoute = useCallback(
    async (routeId: string) => {
      if (!user) return null;

      // Buscar a rota completa do estado local
      const route = routes.find((r) => r.id === routeId);
      if (!route) {
        console.error('Rota não encontrada:', routeId);
        return null;
      }

      const now = new Date().toISOString();
      const stops = route.stops as DeliveryStop[];
      
      // Calcular distância e duração corretas
      const totalDistance = route.optimized_distance || route.original_distance || 0;
      const totalDuration = route.optimized_duration || route.original_duration || 0;
      
      // Calcular tempo real da rota (se tiver started_at)
      let realDuration = totalDuration;
      if (route.started_at) {
        const startTime = new Date(route.started_at).getTime();
        const endTime = new Date(now).getTime();
        realDuration = Math.round((endTime - startTime) / 1000); // em segundos
      }

      // Converter stops para array de strings de endereço para a tabela deliveries
      const stopAddresses = stops.slice(1).map((s) => s.address || 'Endereço não informado');

      try {
        // Atualizar rota para completed
        const { error: routeError } = await supabase
          .from('routes')
          .update({
            status: 'completed',
            finished_at: now,
          })
          .eq('id', routeId)
          .eq('user_id', user.id);

        if (routeError) throw routeError;

        // Criar entrega com dados completos
        const { data: delivery, error: deliveryError } = await supabase
          .from('deliveries')
          .insert({
            user_id: user.id,
            route_id: routeId,
            amount: 0,
            status: 'completed',
            origin: stops[0]?.address || 'Origem não informada',
            stops: stopAddresses,
            total_distance: totalDistance,
            total_duration: realDuration,
            optimized: route.optimized || false,
          })
          .select()
          .single();

        if (deliveryError) throw deliveryError;

        // Optimistic update - remover rota ativa e adicionar entrega
        setRoutes((prev) =>
          prev.map((r) =>
            r.id === routeId
              ? { ...r, status: 'completed' as const, finished_at: now }
              : r
          )
        );
        setDeliveries((prev) => [delivery, ...prev]);

        return delivery;
      } catch (error) {
        console.error('Erro ao finalizar rota:', error);
        // Fallback: refetch
        await Promise.all([fetchRoutes(), fetchDeliveries()]);
        return null;
      }
    },
    [user, routes, fetchRoutes, fetchDeliveries]
  );

  // Criar entrega manualmente (com optimistic update)
  const createDelivery = useCallback(
    async (deliveryData: Omit<Delivery, 'id' | 'user_id' | 'created_at'>) => {
      if (!user) return null;

      try {
        const { data, error } = await supabase
          .from('deliveries')
          .insert({
            user_id: user.id,
            ...deliveryData,
          })
          .select()
          .single();

        if (error) throw error;

        // Optimistic update
        setDeliveries((prev) => [data, ...prev]);
        return data;
      } catch (error) {
        console.error('Erro ao criar entrega:', error);
        // Fallback: refetch
        await fetchDeliveries();
        return null;
      }
    },
    [user, fetchDeliveries]
  );

  // Atualizar valor de ganho (com optimistic update)
  const updateDeliveryAmount = useCallback(
    async (deliveryId: string, amount: number) => {
      if (!user) return null;

      // Optimistic update
      setDeliveries((prev) =>
        prev.map((delivery) =>
          delivery.id === deliveryId ? { ...delivery, amount } : delivery
        )
      );

      try {
        const { data, error } = await supabase
          .from('deliveries')
          .update({ amount })
          .eq('id', deliveryId)
          .eq('user_id', user.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } catch (error) {
        console.error('Erro ao atualizar ganho:', error);
        // Fallback: refetch
        await fetchDeliveries();
        return null;
      }
    },
    [user, fetchDeliveries]
  );

  // Deletar entrega (com optimistic update)
  const deleteDelivery = useCallback(
    async (deliveryId: string) => {
      if (!user) return false;

      // Optimistic update
      setDeliveries((prev) => prev.filter((d) => d.id !== deliveryId));

      try {
        const { error } = await supabase
          .from('deliveries')
          .delete()
          .eq('id', deliveryId)
          .eq('user_id', user.id);

        if (error) throw error;
        return true;
      } catch (error) {
        console.error('Erro ao deletar entrega:', error);
        // Fallback: refetch
        await fetchDeliveries();
        return false;
      }
    },
    [user, fetchDeliveries]
  );

  // Deletar rota (com optimistic update)
  const deleteRoute = useCallback(
    async (routeId: string) => {
      if (!user) return false;

      // Optimistic update
      setRoutes((prev) => prev.filter((r) => r.id !== routeId));

      try {
        const { error } = await supabase
          .from('routes')
          .delete()
          .eq('id', routeId)
          .eq('user_id', user.id);

        if (error) throw error;
        return true;
      } catch (error) {
        console.error('Erro ao deletar rota:', error);
        // Fallback: refetch
        await fetchRoutes();
        return false;
      }
    },
    [user, fetchRoutes]
  );

  // Carregar dados iniciais
  useEffect(() => {
    if (!user) {
      setLoading(false);
      setRoutes([]);
      setDeliveries([]);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchRoutes(), fetchDeliveries()]);
      setLoading(false);
    };

    loadData();
  }, [user, fetchRoutes, fetchDeliveries]);

  // Configurar UMA ÚNICA subscription realtime para routes e deliveries
  useEffect(() => {
    if (!user) return;

    // Subscribe para mudanças em routes
    const routesChannel = supabase
      .channel('routes-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'routes',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          fetchRoutes();
        }
      )
      .subscribe();

    // Subscribe para mudanças em deliveries
    const deliveriesChannel = supabase
      .channel('deliveries-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'deliveries',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          fetchDeliveries();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(routesChannel);
      supabase.removeChannel(deliveriesChannel);
    };
  }, [user, fetchRoutes, fetchDeliveries]);

  const value = {
    routes,
    deliveries,
    loading,
    stats,
    saveRoute,
    updateRoute,
    finishRoute,
    createDelivery,
    updateDeliveryAmount,
    deleteDelivery,
    deleteRoute,
    fetchRoutes,
    fetchDeliveries,
  };

  return (
    <DeliveryDataContext.Provider value={value}>
      {children}
    </DeliveryDataContext.Provider>
  );
}

export function useDeliveryContext() {
  const context = useContext(DeliveryDataContext);
  if (context === undefined) {
    throw new Error('useDeliveryContext deve ser usado dentro de um DeliveryDataProvider');
  }
  return context;
}
